/**
 * src/types/charts/gauge.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Gauge / speedometer chart — semi-circle arc (180°) with a needle.
 * A gauge always has exactly ONE data point — the current reading.
 * `zones` paint colored segments on the arc (e.g. green/amber/red ranges).
 */

import type { HexColor } from '../core/primitives';
import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type GaugeAnimationStyle = 'sweep' | 'bounce' | 'classic';

export const GAUGE_ANIMATION_STYLES: GaugeAnimationStyle[] = ['sweep', 'bounce', 'classic'];

// Gauge has no per-point animation override.
export const GAUGE_ELEMENT_PANEL_STYLES: GaugeAnimationStyle[] = [];


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A gauge always has exactly ONE data point — the reading.
 * `value` must be within [spacing.minValue, spacing.maxValue].
 */
export interface GaugeDataPoint extends ChartDataPoint {
  chartType: 'gauge';
  value:     number;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

/** A single colored zone on the gauge arc. */
export interface GaugeZone {
  min:    number;    // zone start value (inclusive)
  max:    number;    // zone end value (exclusive, except the last zone)
  color:  HexColor;
  label?: string;    // optional label (e.g. 'Low', 'Medium', 'High')
}

export interface GaugeSpacingConfig {
  chartType:    'gauge';
  minValue:     number;       // gauge scale minimum. Default: 0.
  maxValue:     number;       // gauge scale maximum. Default: 100.
  arcThickness: number;       // arc stroke width px. Range: 20–120. Default: 60.
  needleLength: number;       // needle length as fraction of arc radius. Range: 0.4–0.9. Default: 0.75.
  showZones:    boolean;      // paint color zones on the arc. Default: true.
  zones:        GaugeZone[];  // color zone definitions (sorted by min, ascending).
  showValue:    boolean;      // show numeric value in gauge center. Default: true.
  showMinMax:   boolean;      // show min/max labels at arc ends. Default: true.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const GAUGE_DEFAULTS: ChartConfigBase<GaugeSpacingConfig> = {
  chartType: 'gauge',
  content: {
    titles:    [makeTitle('Performance Score')],
    subtitles: [makeSubtitle('Current Status')],
    infoBoxes: [],
  },
  animation:  { duration: 3, style: 'sweep', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 50 },
    chartMargin:   { top: 120, right: 80, bottom: 80, left: 80 },
  },
  // No axes — gauge has no traditional x/y axes.
  legend:     { ...DEFAULT_LEGEND, visible: false },
  dataLabels: { ...DEFAULT_DATA_LABELS, visible: false },
  spacing: {
    chartType:    'gauge',
    minValue:     0,
    maxValue:     100,
    arcThickness: 60,
    needleLength: 0.75,
    showZones:    true,
    zones: [
      { min: 0,  max: 33,  color: '#22c55e', label: 'Low'    },
      { min: 33, max: 66,  color: '#f59e0b', label: 'Medium' },
      { min: 66, max: 100, color: '#ef4444', label: 'High'   },
    ],
    showValue:  true,
    showMinMax: true,
  },
  export: { quality: '1080p', isLooping: false, canvasFormat: '16:9' },
};
